import { Parser } from '~/api';
import { Egg } from '~/api/models';
import type { EggFirewallConfig } from '~/api/models/Egg';
import { FavoriteFile } from '~/composables/files/useFavorites';
import { useModelsStore } from '~/stores/models';
import RequestService from './request';

interface CreateEggRequest {
    name: string;
    tag: string;
    description: string;
    docker_images: Record<string, string>;
    startup: string;
    config_from: number;
    config_logs: string;
    config_files: string;
    config_startup: string;
    config_stop: string;
    custom_config: string;
    wispignore: string;
    favorite_files: FavoriteFile[];
    firewall_config?: EggFirewallConfig | null;
}

interface ImportEggRequest {
    import_file: File;
    nest_id: number;
}

interface ImportUpdateEggRequest {
    import_file: File;
}

interface UpdateScriptsRequest {
    scriptInstall: string;
    scriptIsPrivileged: boolean;
    scriptEntry: string;
    scriptContainer: string;
    copyScriptFrom?: number;

    scriptUpdate: string;
    scriptUpdateIsPrivileged: boolean;
    scriptUpdateEntry: string;
    scriptUpdateContainer: string;
    copyUpdateScriptFrom?: number;
}

interface AssetRequest {
    asset: File;
}

interface UpdateExportDataRequest {
    export_data: Record<string, unknown> | null;
}

class EggsService {
    getAll(req: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get('/nests/:nest/eggs', {
            include: ['serversCount'],
            ...req,
        }).then(Parser.parse);
    }

    get(): Promise<Egg> {
        return RequestService.get('/nests/:nest/eggs/:egg').then(Parser.parse);
    }

    create(data: CreateEggRequest): Promise<Egg> {
        return RequestService.post('/nests/:nest/eggs', {
            ...data,
            include: ['nest'],
        }).then(Parser.parse);
    }

    import(data: ImportEggRequest) {
        const formData = new FormData();
        formData.append('import_file', data.import_file);

        return RequestService.post(`/nests/${data.nest_id}/eggs/import?include=nest`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(Parser.parse);
    }

    importUpdate(data: ImportUpdateEggRequest) {
        const formData = new FormData();
        formData.append('import_file', data.import_file);

        return RequestService.post('/nests/:nest/eggs/:egg/import?include=nest', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
            .then(Parser.parse)
            .then((egg) => {
                useModelsStore().refreshModel('egg');

                return egg;
            });
    }

    update(data: CreateEggRequest): Promise<Egg> {
        return RequestService.put('/nests/:nest/eggs/:egg', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    updateScripts(data: UpdateScriptsRequest): Promise<Egg> {
        return RequestService.put('/nests/:nest/eggs/:egg/scripts', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    private assetToFormData(data: AssetRequest) {
        const formData = new FormData();
        if (data.asset) formData.append('asset', data.asset);
        return formData;
    }

    updateThumbnail(data: AssetRequest): Promise<Egg> {
        return RequestService.post('/nests/:nest/eggs/:egg/thumbnail', this.assetToFormData(data), {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
            .then(Parser.parse)
            .then(RequestService.updateModelBinding);
    }

    updateFirewallConfig(data: { firewall_config: EggFirewallConfig | null }): Promise<Egg> {
        return RequestService.put('/nests/:nest/eggs/:egg/firewall-config', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    syncFirewallToServers(): Promise<Egg> {
        return RequestService.post('/nests/:nest/eggs/:egg/firewall-config/sync').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    delete(): Promise<void> {
        return RequestService.delete('/nests/:nest/eggs/:egg');
    }

    syncFromSource(): Promise<Egg> {
        return RequestService.post('/nests/:nest/eggs/:egg/sync-from-source').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    detachFromSource(): Promise<Egg> {
        return RequestService.post('/nests/:nest/eggs/:egg/detach-from-source').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    attachToSource(data: { source_key: string; source_id: string }): Promise<Egg> {
        return RequestService.post('/nests/:nest/eggs/:egg/attach-to-source', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    updateExportData(data: UpdateExportDataRequest): Promise<Egg> {
        return RequestService.put('/nests/:nest/eggs/:egg/export-data', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }
}

export default new EggsService();
